// Leader process handlers. The live branch calls the cloud functions; the mock branch returns local
// fixtures (see ./mocks/leaderMocks) so the org flow and the My people page work in mock mode.

import { LeaderErrorCode } from '../../../../pure-js/enums/LeaderErrorCode'
import { toError } from '../../../../pure-js/libs/Common'
import type {
  GetLeaderInsightsRequest,
  GetLeaderInsightsResponse,
  GetLeaderScopeRequest,
  GetLeaderScopeResponse,
  SetLeaderScopeRequest,
  SetLeaderScopeResponse
} from '../../../../pure-js/types/Leader'
import { loadCloudFunctionsHandler } from '../handlers'
import { handleRequest } from './config'
import { MOCK_LEADER_SCOPE_MEMBERS, buildMockLeaderInsightsResult, buildMockLeaderScope } from './mocks/leaderMocks'

export const getLeaderScope = async (request: GetLeaderScopeRequest): Promise<GetLeaderScopeResponse> =>
  handleRequest<GetLeaderScopeResponse>(
    async () => ({
      success: true,
      data: { scope: buildMockLeaderScope(request), members: MOCK_LEADER_SCOPE_MEMBERS }
    }),
    async () => (await loadCloudFunctionsHandler()).getLeaderScope(request)
  )

export const setLeaderScope = async (request: SetLeaderScopeRequest): Promise<SetLeaderScopeResponse> =>
  handleRequest<SetLeaderScopeResponse>(
    async () => ({ success: true, data: { scope: buildMockLeaderScope(request) } }),
    async () => (await loadCloudFunctionsHandler()).setLeaderScope(request)
  )

export const getLeaderInsights = async (request: GetLeaderInsightsRequest): Promise<GetLeaderInsightsResponse> =>
  handleRequest<GetLeaderInsightsResponse>(
    async () => ({ success: true, data: buildMockLeaderInsightsResult() }),
    async () => (await loadCloudFunctionsHandler()).getLeaderInsights(request)
  )

const LEADER_UNKNOWN_ERROR_TRANSLATION_KEY = 'org.leaderScope.saveError'

const LEADER_ERROR_TRANSLATION_KEYS: Record<LeaderErrorCode, string> = {
  [LeaderErrorCode.FEATURE_DISABLED]: 'org.leaderScope.featureDisabled',
  [LeaderErrorCode.LEADER_ROLE_REQUIRED]: 'gamer.myPeople.state.disabled.body',
  [LeaderErrorCode.TARGET_NOT_LEADER]: 'org.leaderScope.loadError',
  [LeaderErrorCode.SCOPE_USER_NOT_PARTICIPANT]: 'org.leaderScope.saveError',
  [LeaderErrorCode.SCOPE_CONTAINS_LEADER]: 'org.leaderScope.saveError',
  [LeaderErrorCode.SCOPE_TOO_LARGE]: 'org.leaderScope.tooMany',
  [LeaderErrorCode.SCOPE_INVALID]: 'org.leaderScope.saveError'
}

/** The callable's error message carries the wire code; anything unrecognised reads as a failed save. */
export const resolveLeaderErrorTranslationKey = (error: unknown): string => {
  const errorMessage = toError(error).message
  const matchedCode = Object.values(LeaderErrorCode).find((code) => errorMessage.includes(code))
  return matchedCode ? LEADER_ERROR_TRANSLATION_KEYS[matchedCode] : LEADER_UNKNOWN_ERROR_TRANSLATION_KEY
}
